import {
  Avatar,
  Button,
  Callout,
  CheckboxField,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIllustration,
  ModalOverlay,
} from '@dev-spendesk/grapes';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import * as Yup from 'yup';

import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routes } from 'src/core/constants/routes';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import wiseLogo from 'src/core/modules/integrations/assets/integration-logos/wise.svg';
import spendeskLogo from 'src/core/modules/integrations/assets/spendesk.svg';
import { useUpdateKycReview } from 'src/core/modules/invoices/transfer/hooks/api/useUpdateKycReview';
import { track, AnalyticEventName } from 'src/core/utils/analytics';

import { type BusinessRepresentative } from './BusinessRepresentative';
import { BusinessRepresentativeForm } from './BusinessRepresentativeForm';
import { useUser } from '../../../../app/hooks/useUser';
import { useGetWiseKycStatus } from '../../../../invoices/transfer';
import { useCreateWiseBusinessProfile } from '../../../hooks';

type ModalState = 'open' | 'selectRepresentative' | 'waitingForKycReview';

const REFRESH_INTERVAL_MS = 3000;

export const WiseConnectionModal = ({
  handleCloseModalButton,
}: {
  handleCloseModalButton: () => void;
}) => {
  useEffect(() => {
    track(AnalyticEventName.INTEGRATIONS_WISE_CONNECT_TO_WISE_MODAL_OPENED, {});
  }, []);

  const { t } = useTranslation('global');

  const [modalState, setModalState] = useState<ModalState>('open');

  const [acceptedTermsChecked, setAcceptedTermsChecked] = useState(false);

  const { pushNotif } = useNotifications();

  const { company: companyId } = useParams(
    routes.WISE_INTEGRATION_LANDING.path,
  );

  const { mutateAsync: createBusinessProfileMutation } =
    useCreateWiseBusinessProfile({
      companyId,
    });

  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  const spendeskRedirectUrl = `${baseUrl}/app/${companyId}/company/integrations/all/wise`;

  const { mutateAsync: updateKycReview } = useUpdateKycReview({
    companyId,
    redirectUserTo: `${spendeskRedirectUrl}?status=success`,
  });

  const { data: kycStatus } = useGetWiseKycStatus({
    companyId,
    enabled: modalState === 'waitingForKycReview',
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const [formLoading, setFormLoading] = useState(false);

  const {
    first_name: firstName,
    last_name: lastName,
    email,
    birth_date: birthDate,
    birth_location_city: city,
    mobile_no: phoneNumber,
    mobile_ext: mobileExtension,
  } = useUser();

  const form = useFormik<BusinessRepresentative>({
    initialValues: {
      firstName,
      lastName,
      dateOfBirth: birthDate ? new Date(birthDate) : new Date(),
      address: {
        addressFirstLine: '',
        city: city ?? '',
        countryIso2Code: 'GB',
        postCode: '',
      },
      email,
      phoneNumber:
        phoneNumber && mobileExtension
          ? `+${mobileExtension}${phoneNumber}`
          : '',
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string()
        .min(1)
        .required(t('forms.errorMessages.required')),
      lastName: Yup.string().min(1).required(t('forms.errorMessages.required')),
      dateOfBirth: Yup.date().required(t('forms.errorMessages.required')),
      address: Yup.object().shape({
        addressFirstLine: Yup.string()
          .min(1)
          .required(t('forms.errorMessages.required')),
        city: Yup.string().min(1).required(t('forms.errorMessages.required')),
        countryIso2Code: Yup.string()
          .min(1)
          .required(t('forms.errorMessages.required')),
        postCode: Yup.string()
          .min(1)
          .required(t('forms.errorMessages.required')),
      }),
      email: Yup.string()
        .email(t('forms.errorMessages.invalid'))
        .required(t('forms.errorMessages.required')),
      phoneNumber: Yup.string()
        .min(5, t('forms.errorMessages.required'))
        .required(t('forms.errorMessages.required')),
    }),
    onSubmit: async (formValues) => {
      try {
        setFormLoading(true);

        await createBusinessProfileMutation({
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          preferredName: formValues.firstName,
          dateOfBirth: format(formValues.dateOfBirth, 'yyyy-MM-dd'),
          address: formValues.address,
          contactDetails: {
            email: formValues.email,
            phoneNumber: formValues.phoneNumber,
          },
        });

        setModalState('waitingForKycReview');

        pushNotif({
          type: NotificationType.Success,
          message: t(
            'wiseIntegrationLandingPage.successNotificationConnectTitle',
          ),
        });
      } catch {
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'wiseIntegrationLandingPage.errorNotificationConnectTitle',
          ),
        });
      } finally {
        setFormLoading(false);
      }
    },
  });

  const handleConnectModalButton = async () => {
    track(AnalyticEventName.INTEGRATIONS_WISE_CONTINUE_BUTTON_CLICKED, {});
    setModalState('selectRepresentative');
  };

  const handleSubmitFormButton = async () => {
    track(AnalyticEventName.INTEGRATIONS_WISE_WISE_KYB_OPENED, {});
    form.submitForm();
  };

  const handleRedirectKycReview = async () => {
    try {
      const { redirectUrl } = await updateKycReview();

      window.open(redirectUrl, '_blank');

      window.location.href = spendeskRedirectUrl;
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('wiseIntegrationLandingPage.errorNotificationConnectTitle'),
      });
    }
  };

  return (
    <ModalOverlay isOpen>
      <ModalContent>
        <ModalHeaderWithIllustration
          title={t('connectToWiseModal.title')}
          illustrationHeight="90px"
          illustration={
            <div className="mt-32 flex flex-row items-center gap-16">
              <Avatar
                variant="square"
                className="flex"
                src={spendeskLogo}
                text="Spendesk Logo"
                size={56}
              />
              <Icon className="flex" size="l" name="arrow-left-right" />
              <Avatar
                variant="square"
                className="flex"
                src={wiseLogo}
                text="Wise Logo"
                size={56}
              />
            </div>
          }
        />

        {modalState === 'open' ? (
          <ModalBody>
            <div className="flex flex-col items-center gap-16">
              <p className="font-medium">
                {t('connectToWiseModal.firstParagraph')}
              </p>

              <p>{t('connectToWiseModal.secondParagraph')}</p>

              <Callout
                variant="info"
                title={t('connectToWiseModal.calloutTitle')}
              >
                <Trans i18nKey="connectToWiseModal.calloutDescriptionWithLink">
                  <a href="https://helpcenter.spendesk.com/en/articles/13004369-uk-wise-integration-guide">
                    .
                  </a>
                </Trans>
              </Callout>

              <CheckboxField
                label={
                  <Trans i18nKey="connectToWiseModal.acceptTermsCheckboxLabel">
                    <a
                      href="https://wise.com/gb/legal/terms-of-use-business"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      -
                    </a>
                    <a
                      href="https://wise.com/gb/legal/privacy-notices"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      -
                    </a>
                  </Trans>
                }
                isChecked={acceptedTermsChecked}
                onChange={(event) =>
                  setAcceptedTermsChecked(event.target.checked)
                }
              />

              <p>{t('connectToWiseModal.wiseCompanyInfo')}</p>
            </div>
          </ModalBody>
        ) : null}

        {modalState === 'selectRepresentative' ? (
          <ModalBody>
            <div className="flex flex-col items-stretch gap-16">
              <p>{t('connectToWiseModal.businessRepresentativeFormTitle')}</p>

              <BusinessRepresentativeForm form={form} />
            </div>
          </ModalBody>
        ) : null}

        {modalState === 'waitingForKycReview' ? (
          <ModalBody>
            <div className="flex flex-col items-stretch gap-16">
              <p>{t('connectToWiseModal.secondParagraph')}</p>
            </div>
          </ModalBody>
        ) : null}

        <ModalFooter>
          <Button
            text={t('connectToWiseModal.firstButtonText')}
            variant="secondaryNeutral"
            onClick={handleCloseModalButton}
          />

          {modalState === 'open' ? (
            <Button
              text={t('connectToWiseModal.secondButtonText')}
              variant="primaryBrand"
              isDisabled={!acceptedTermsChecked}
              onClick={handleConnectModalButton}
            />
          ) : null}

          {modalState === 'selectRepresentative' ? (
            <Button
              text={t('connectToWiseModal.secondButtonText')}
              variant="primaryBrand"
              isDisabled={form.touched && !form.isValid}
              onClick={handleSubmitFormButton}
              isLoading={formLoading}
            />
          ) : null}

          {modalState === 'waitingForKycReview' ? (
            <Button
              isDisabled={kycStatus !== 'WAITING_CUSTOMER_INPUT'}
              isLoading={kycStatus !== 'WAITING_CUSTOMER_INPUT'}
              text={t('connectToWiseModal.secondButtonText')}
              variant="primaryBrand"
              onClick={handleRedirectKycReview}
            />
          ) : null}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};
